import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useQuery } from "@tanstack/react-query";
import { FC, useCallback, useEffect, useState } from "react";
import { FormControl } from "react-bootstrap";
import { B2Form, B2InputGroup } from "src/shared/@hdb/biome-2";
import { getAssetsByLocationAndType } from "../../../../../core/api/services/dashboard.services";
import ModalComponent from "../../../../../core/components/modal/modal.component";
import TableComponent from "../../../../../core/components/table/table.component";
import TableSort from "../../../../../shared/helpers/table-sort";
import capitalizeFirst from "../../../../../shared/helpers/upper-case";

interface LuminaryModalComponentTypes {
  isLuminaryModalOpen: boolean;
  blockCode: any;
  handleCloseModal: any;
}
const LuminaryModalComponent: FC<LuminaryModalComponentTypes> = ({
  isLuminaryModalOpen,
  blockCode,
  handleCloseModal,
}) => {
  const [columns, setColumns] = useState([
    {
      title: "Asset ID",
      label: "assetId",
      type: "link",
      link: "/asset",
      linkableField: "assetId",
      isSorted: true,
      isOpenInNewTable: true,
    },
    {
      title: "Display Name",
      label: "sensorDisplayName",
      width: "13rem",
    },
    {
      title: "Address",
      label: "address",
    },
    {
      title: "Last Data Received",
      label: "lastDataReceived",
      width: "10rem",
      type: "datetime",
    },
  ]);

  const [SortBy, setSortBy] = useState<any>(capitalizeFirst("assetId"));
  const [SortDirection, setSortDirection] = useState<any>("desc");
  const equipmentQuantityType = 2;
  const [luminaryPageNo, setLuminaryPageNo] = useState(1);
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const pageSize = 10;

  const handleChangeSort = useCallback(
    async (sortBy: string, sortDirection: any) => {
      if (!sortBy || !sortDirection) return;
      setSortBy(sortBy);
      setSortDirection(sortDirection);

      const copyColumns: any = await TableSort(columns, sortBy);
      setColumns(copyColumns);
    },
    []
  );

  const handleLuminaryChangePage = (page: number): void => {
    setLuminaryPageNo(page);
  };

  const {
    data: luminarysByBlockCode,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: [
      "getAssetsByLocationAndType",
      equipmentQuantityType,
      luminaryPageNo,
      pageSize,
      SortDirection,
      SortBy,
      blockCode,
      debouncedSearch
    ],
    queryFn: () =>
      getAssetsByLocationAndType(
        equipmentQuantityType,
        luminaryPageNo,
        pageSize,
        SortBy,
        SortDirection,
        null,
        null,
        blockCode,
        debouncedSearch || null
      ),
    enabled: !!isLuminaryModalOpen,
    placeholderData: (prev) => prev,
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();

    setLuminaryPageNo(1);
    setDebouncedSearch(searchValue)
  };

  const handleClear = () => {
    setSearchValue("");
  };

  const handleKeyUp = (e: any) => {
    if (e.keyCode === 8 && !searchValue) {
      handleClear();
    }
  };

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(searchValue), 1000);
    return () => clearTimeout(handle);
  }, [searchValue]);

  useEffect(() => {
    setLuminaryPageNo(1);
  }, [debouncedSearch]);

  return (
    <>
      {isLuminaryModalOpen ? (
        <ModalComponent
          showModal={isLuminaryModalOpen}
          handleClose={() => {
            handleCloseModal();
            setSearchValue("");
            setLuminaryPageNo(1);
          }}
          headerTitle="Luminaire"
          isViewMode={true}
          size="xl"
        >
          <div className="inside-table">
            <div className="d-flex justify-content-end align-items-end">
              <B2Form onSubmit={handleSearch}>
                <div
                  className=""
                  style={{
                    display: "flex",
                    width: "300px",
                    marginLeft: "27rem",
                    marginBottom: "20px",
                  }}
                >
                  <B2InputGroup>
                    <FormControl
                      style={{
                        borderRight: searchValue && "none",
                        height: "45px",
                        width: "250px",
                      }}
                      className="search-field"
                      placeholder={"Search Assets"}
                      value={searchValue}
                      onChange={(e: any) => setSearchValue(e.target.value)}
                      onKeyUp={handleKeyUp}
                    />

                    <B2InputGroup.Text>
                      <FontAwesomeIcon icon={faSearch} />
                    </B2InputGroup.Text>
                  </B2InputGroup>
                </div>
              </B2Form>
            </div>
            <TableComponent
              tableHeaders={columns}
              data={luminarysByBlockCode?.data}
              pageSize={pageSize}
              current={luminaryPageNo}
              totalNumberOfRecords={luminarysByBlockCode?.numberOfRecords}
              onChangePage={handleLuminaryChangePage}
              onChangeSort={handleChangeSort}
              isLoadingData={isLoading || isFetching}
            />
          </div>
        </ModalComponent>
      ) : null}
    </>
  );
};

export default LuminaryModalComponent;
